/**
 * Plan lookup.
 *
 * Its own module rather than part of the package index so that enforcement and
 * display logic can use it without importing the index — which re-exports them
 * — and creating an import cycle.
 */

import { PLANS } from "./config";
import { type Plan, SubscriptionTier } from "./types";

const BY_TIER = new Map<SubscriptionTier, Plan>(PLANS.map((p) => [p.key, p]));

/** The plan for a tier. Throws on an unknown tier — see {@link normaliseTier}. */
export function planFor(tier: SubscriptionTier): Plan {
  const plan = BY_TIER.get(tier);
  if (!plan) {
    throw new Error(`No plan defined for tier "${tier}".`);
  }
  return plan;
}

/**
 * Coerce untrusted input (a DynamoDB field, a URL param) to a real tier.
 *
 * Falls back to the CHEAPEST tier deliberately. Guessing too low produces a
 * user who is wrongly refused and complains — visible. Guessing too high hands
 * out unmetered spend silently.
 */
export function normaliseTier(value: unknown): SubscriptionTier {
  return typeof value === "string" && BY_TIER.has(value as SubscriptionTier)
    ? (value as SubscriptionTier)
    : SubscriptionTier.FREE;
}

/**
 * Days of Timeline history a tier keeps, or `null` for forever.
 *
 * Here rather than in the history code so the plan page, the pricing table
 * and the retention job all read one number.
 */
export function historyRetentionDays(tier: SubscriptionTier): number | null {
  return planFor(tier).limits.historyRetentionDays;
}

/** Entries per draft that may carry a name on a tier, or `null` for no cap. */
export function namedVersionsPerDraft(tier: SubscriptionTier): number | null {
  return planFor(tier).limits.namedVersionsPerDraft;
}

/** Tasks a tier may keep active at once, or `null` for no cap. */
export function activeTaskLimit(tier: SubscriptionTier): number | null {
  return planFor(tier).limits.activeTasks;
}

/** Workflows a tier may keep, or `null` for no cap. */
export function workflowLimit(tier: SubscriptionTier): number | null {
  return planFor(tier).limits.workflows;
}

/** The shortest repeat a task schedule may use on a tier. */
export function shortestTaskRepeat(tier: SubscriptionTier): "daily" | "weekly" {
  return planFor(tier).limits.shortestTaskRepeat;
}
